import React, { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { motion } from 'framer-motion'
import { useAuthStore } from './stores/authStore'
import { useWebSocketStore } from './stores/websocketStore'
import { api } from './lib/api'
import { Menu, Bell, Search, LogOut, User, ChevronDown } from 'lucide-react'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import MenuPage from './pages/MenuPage'
import MenuImportPage from './pages/MenuImportPage'
import TablesPage from './pages/TablesPage'
import OrdersPage from './pages/OrdersPageImproved'
import AnalyticsPage from './pages/AnalyticsPage'
import CustomerMenuPage from './pages/CustomerMenuPage'
import { CheckoutPage } from './pages/CheckoutPage'
import { OrderSuccessPage } from './pages/OrderSuccessPage'
import { OrderTrackingPage } from './pages/OrderTrackingPage'
import { PaymentPage } from './pages/PaymentPage'
import { PaymentSuccessPage } from './pages/PaymentSuccessPage'
import { PaymentSettingsPage } from './pages/PaymentSettingsPage'
import { SatispayPaymentPage } from './pages/SatispayPaymentPage'
import { KDSPage } from './pages/KDSPage'
import AdvancedAnalyticsPage from './pages/AdvancedAnalyticsPage'
import CohortAnalysisPage from './pages/CohortAnalysisPage'
import MenuEngineeringPage from './pages/MenuEngineeringPage'
import InventoryDashboardPage from './pages/InventoryDashboardPage'
import IngredientsListPage from './pages/IngredientsListPage'
import IngredientFormPage from './pages/IngredientFormPage'
import PurchaseRecordingPage from './pages/PurchaseRecordingPage'
import MovementHistoryPage from './pages/MovementHistoryPage'
import DocumentationPage from './pages/DocumentationPage'
import SettingsPage from './pages/dashboard/SettingsPage'
import Logo from './components/Logo'
import { AdminAuthGuard } from './components/admin/AdminAuthGuard'
import { AdminLayout } from './components/admin/AdminLayout'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import AdminRestaurantsPage from './pages/admin/AdminRestaurantsPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage'
import AdminRevenuePage from './pages/admin/AdminRevenuePage'
import { NotificationContainer } from './components/NotificationContainer'
import { useOrderNotifications } from './hooks/useOrderNotifications'
import { CookieConsent } from './components/CookieConsent'
import { Sidebar } from './components/Sidebar'
import { ThemeProvider } from './contexts/ThemeContext'
import { ThemeToggle } from './components/ThemeToggle'
import { SoundToggle } from './components/SoundToggle'
import InstallPWA from './components/InstallPWA'

// Dashboard Layout with Enterprise Premium Design
function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768
    }
    return false
  })
  const [showUserMenu, setShowUserMenu] = React.useState(false)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1f] transition-colors duration-300">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'md:pl-72' : 'md:pl-[72px]'}`}>
        {/* Top Bar - Premium Header */}
        <header className="dashboard-header sticky top-0 z-30 bg-white/80 dark:bg-[#1e1e24]/90 backdrop-blur-xl border-b border-gray-200 dark:border-white/[0.08] transition-colors duration-300">
          <div className="px-4 md:px-6 h-16 flex items-center justify-between gap-4">
            {/* Left: Mobile Menu + Restaurant Name */}
            <div className="flex items-center gap-4">
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all md:hidden"
              >
                <Menu size={20} />
              </button>

              {/* Restaurant Name */}
              <div className="hidden sm:block">
                <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white truncate max-w-[200px]">
                  {user?.restaurantName}
                </h2>
              </div>
            </div>

            {/* Center: Search (hidden on mobile) */}
            <div className="hidden lg:flex flex-1 max-w-md mx-auto">
              <div className="relative w-full">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/30" />
                <input
                  type="text"
                  placeholder="Cerca ordini, prodotti..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none focus:border-orange-500/50 focus:bg-white dark:focus:bg-white/[0.06] transition-all"
                />
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Sound Toggle */}
              <SoundToggle />

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notifications */}
              <button className="relative p-2 rounded-lg text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all">
                <Bell size={18} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-orange-500 rounded-full" />
              </button>

              {/* Divider */}
              <div className="w-px h-8 bg-gray-200 dark:bg-white/[0.06] mx-2 hidden sm:block" />

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-1.5 pr-3 rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-[13px] font-medium text-gray-900 dark:text-white leading-tight">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-white/40">Proprietario</p>
                  </div>
                  <ChevronDown size={14} className="text-gray-400 dark:text-white/40 hidden sm:block" />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-[#25252d] border border-gray-200 dark:border-white/[0.1] rounded-xl shadow-2xl overflow-hidden z-50"
                    >
                      <div className="p-3 border-b border-gray-100 dark:border-white/[0.06]">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-gray-500 dark:text-white/40 truncate">{user?.email}</p>
                      </div>
                      <div className="p-1.5">
                        <Link
                          to="/dashboard/settings"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
                        >
                          <User size={15} />
                          <span>Profilo</span>
                        </Link>
                        <button
                          onClick={() => { logout(); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                        >
                          <LogOut size={15} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  )
}

// Dashboard Home - Enterprise Premium Design
function DashboardHome() {
  const { user } = useAuthStore()
  const [stats, setStats] = React.useState({
    productsCount: 0,
    activeTables: 0,
    todayOrders: 0,
    activeOrders: 0,
    todayRevenue: 0,
    yesterdayRevenue: 0,
  })
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    if (!user?.restaurantId) return

    try {
      const [productsRes, ordersRes] = await Promise.all([
        api.get(`/api/menu/products?restaurantId=${user.restaurantId}`),
        api.get('/api/orders'),
      ])

      const products = productsRes.data
      const orders = ordersRes.data

      const today = new Date().toISOString().split('T')[0]
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]

      const todayOrders = orders.filter((o: any) => o.createdAt.startsWith(today))
      const yesterdayOrders = orders.filter((o: any) => o.createdAt.startsWith(yesterday))
      const activeOrders = orders.filter((o: any) => ['SUBMITTED', 'PREPARING', 'READY'].includes(o.status))
      const todayRevenue = todayOrders.reduce((sum: number, o: any) => sum + o.total, 0)
      const yesterdayRevenue = yesterdayOrders.reduce((sum: number, o: any) => sum + o.total, 0)

      setStats({
        productsCount: products.length,
        activeTables: 0,
        todayOrders: todayOrders.length,
        activeOrders: activeOrders.length,
        todayRevenue,
        yesterdayRevenue,
      })
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRevenueChange = () => {
    if (stats.yesterdayRevenue === 0) return null
    const change = ((stats.todayRevenue - stats.yesterdayRevenue) / stats.yesterdayRevenue) * 100
    return change.toFixed(1)
  }

  const revenueChange = getRevenueChange()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 dark:text-white/40 text-sm">Caricamento dashboard...</p>
        </div>
      </div>
    )
  }

  const isNewUser = stats.productsCount === 0 && stats.todayOrders === 0

  const statCards = [
    {
      label: 'Prodotti',
      value: stats.productsCount,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      color: 'from-blue-500/10 dark:from-blue-500/20 to-blue-500/5',
      iconColor: 'text-blue-500 dark:text-blue-400',
      borderColor: 'border-blue-500/20',
    },
    {
      label: 'Ordini Attivi',
      value: stats.activeOrders,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      ),
      color: 'from-orange-500/10 dark:from-orange-500/20 to-orange-500/5',
      iconColor: 'text-orange-500 dark:text-orange-400',
      borderColor: 'border-orange-500/20',
      pulse: stats.activeOrders > 0,
    },
    {
      label: 'Ordini Oggi',
      value: stats.todayOrders,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      color: 'from-emerald-500/10 dark:from-emerald-500/20 to-emerald-500/5',
      iconColor: 'text-emerald-500 dark:text-emerald-400',
      borderColor: 'border-emerald-500/20',
    },
    {
      label: 'Incasso Oggi',
      value: `€${stats.todayRevenue.toFixed(2)}`,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
      ),
      color: 'from-violet-500/10 dark:from-violet-500/20 to-violet-500/5',
      iconColor: 'text-violet-500 dark:text-violet-400',
      borderColor: 'border-violet-500/20',
      trend: revenueChange,
    },
  ]

  const quickActions = [
    { to: '/dashboard/orders', label: 'Gestione Ordini', desc: 'Visualizza e gestisci gli ordini', icon: '📋', color: 'orange' },
    { to: '/dashboard/menu', label: 'Gestione Menu', desc: 'Modifica categorie e prodotti', icon: '🍽️', color: 'blue' },
    { to: '/dashboard/tables', label: 'Tavoli & QR', desc: 'Gestisci tavoli e codici QR', icon: '🪑', color: 'emerald' },
    { to: '/dashboard/analytics', label: 'Analytics', desc: 'Statistiche e performance', icon: '📊', color: 'violet' },
  ]

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
            Bentornato, {user?.firstName}
          </h1>
          <p className="text-gray-500 dark:text-white/40 text-sm">
            Ecco cosa sta succedendo nel tuo ristorante oggi
          </p>
        </motion.div>
      </div>

      {/* New User Onboarding */}
      {isNewUser && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-orange-500/10 via-red-500/5 to-transparent border border-orange-500/20"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Inizia a configurare il tuo ristorante
              </h2>
              <p className="text-gray-600 dark:text-white/50 text-sm mb-4">
                Completa questi passaggi per iniziare a ricevere ordini
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/dashboard/menu"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <span>Crea il Menu</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <Link
                  to="/dashboard/tables"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-white/[0.06] hover:bg-gray-200 dark:hover:bg-white/[0.1] text-gray-700 dark:text-white/70 text-sm font-medium rounded-lg transition-colors"
                >
                  Configura Tavoli
                </Link>
              </div>
            </div>
            <div className="hidden md:block text-6xl">🚀</div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className={`relative p-5 rounded-2xl bg-gradient-to-br ${stat.color} border ${stat.borderColor} group hover:scale-[1.02] transition-transform duration-300`}
          >
            {stat.pulse && (
              <span className="absolute top-4 right-4 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
              </span>
            )}
            <div className={`w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/[0.08] flex items-center justify-center mb-4 ${stat.iconColor}`}>
              {stat.icon}
            </div>
            <div className="flex items-end justify-between gap-2">
              <div>
                <p className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <p className="text-xs text-gray-500 dark:text-white/40 mt-1">{stat.label}</p>
              </div>
              {stat.trend && (
                <div className={`flex items-center gap-1 text-xs font-medium ${
                  parseFloat(stat.trend) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  <svg className={`w-3 h-3 ${parseFloat(stat.trend) >= 0 ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span>{Math.abs(parseFloat(stat.trend))}%</span>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={action.to}
              to={action.to}
              className="group p-5 rounded-2xl bg-white dark:bg-[#25252d] border border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.15] hover:bg-gray-50 dark:hover:bg-[#2a2a33] transition-all duration-300 shadow-sm"
            >
              <div className="flex items-start justify-between mb-4">
                <span className="text-3xl group-hover:scale-110 transition-transform duration-300">{action.icon}</span>
                <svg className="w-5 h-5 text-gray-300 dark:text-white/20 group-hover:text-gray-400 dark:group-hover:text-white/40 group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{action.label}</h3>
              <p className="text-xs text-gray-500 dark:text-white/40">{action.desc}</p>
            </Link>
          ))}
        </div>
      </motion.div>
    </div>
  )
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  const { isAuthenticated, user, fetchCurrentUser } = useAuthStore()
  const { connect, disconnect } = useWebSocketStore()

  // Fetch user on app load if authenticated
  useEffect(() => {
    if (isAuthenticated && !user) {
      fetchCurrentUser()
    }
  }, [isAuthenticated, user, fetchCurrentUser])

  // Connect WebSocket for restaurant (staff)
  useEffect(() => {
    if (isAuthenticated && user?.restaurantId) {
      console.log('🔌 Connecting WebSocket for restaurant:', user.restaurantId)
      connect(user.restaurantId)
    } else {
      console.log('🔌 Disconnecting WebSocket')
      disconnect()
    }

    // Cleanup on unmount
    return () => {
      disconnect()
    }
  }, [isAuthenticated, user?.restaurantId, connect, disconnect])

  // Connect WebSocket for customers (global, single connection)
  const customerId = localStorage.getItem('qorder_customer_id') || ''
  useOrderNotifications({
    customerId: customerId || undefined,
    enabled: !!customerId // Only enable if customer ID exists
  })

  return (
    <ThemeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <NotificationContainer />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* Public Routes - No Auth Required */}
          <Route path="/:restaurantSlug/table/:tableNumber" element={<CustomerMenuPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-tracking/:orderId" element={<OrderTrackingPage />} />
          <Route path="/order-success" element={<OrderSuccessPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment/satispay" element={<SatispayPaymentPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardHome />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/menu"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MenuPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/menu/import"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MenuImportPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tables"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TablesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/orders"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OrdersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/analytics"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payments"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PaymentSettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/kds"
            element={
              <ProtectedRoute>
                <KDSPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/analytics-pro"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AdvancedAnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/cohort-analysis"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CohortAnalysisPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/menu-engineering"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MenuEngineeringPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <InventoryDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory/ingredients"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <IngredientsListPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory/ingredients/new"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <IngredientFormPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory/ingredients/:id/edit"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <IngredientFormPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory/purchase"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PurchaseRecordingPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory/movements"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MovementHistoryPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ============================================ */}
          {/* SUPER ADMIN ROUTES */}
          {/* ============================================ */}
          <Route
            path="/admin/dashboard"
            element={
              <AdminAuthGuard>
                <AdminLayout>
                  <AdminDashboardPage />
                </AdminLayout>
              </AdminAuthGuard>
            }
          />
          <Route
            path="/admin/restaurants"
            element={
              <AdminAuthGuard>
                <AdminLayout>
                  <AdminRestaurantsPage />
                </AdminLayout>
              </AdminAuthGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminAuthGuard>
                <AdminLayout>
                  <AdminUsersPage />
                </AdminLayout>
              </AdminAuthGuard>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <AdminAuthGuard>
                <AdminLayout>
                  <AdminSubscriptionsPage />
                </AdminLayout>
              </AdminAuthGuard>
            }
          />
          <Route
            path="/admin/revenue"
            element={
              <AdminAuthGuard>
                <AdminLayout>
                  <AdminRevenuePage />
                </AdminLayout>
              </AdminAuthGuard>
            }
          />
          <Route
            path="/admin/documentation"
            element={
              <AdminAuthGuard>
                <AdminLayout>
                  <DocumentationPage />
                </AdminLayout>
              </AdminAuthGuard>
            }
          />
        </Routes>
        <CookieConsent />
        <InstallPWA />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </ThemeProvider>
  )
}

export default App
